import {
  OTA_IDS,
  PLATFORMS,
} from 'frontend-utils/utils/constants/constant-values';
import {
  COMPARETYPES,
  MEALTYPES,
  MEMBERSHIPTYPES,
  RATE_PARAMS,
  SORT_ORDER,
} from 'lumen/utils/constants/general-constants';
import { DETAIL_TABS } from 'lumen/utils/constants/rate-constants';
import {
  COLUMN_PROPERTIES,
  SUBTYPE_DELIMITER,
  TYPE_ID_NON_REF,
} from 'lumen/utils/constants/rate-strategy-constants';
import { formatUrl } from 'lumen/utils/rates-request';

// When comparing two rows of the same hotelinfo: always put subrows below the row header
// If both rows are subrows: return 0
export function subSortRowsFromSameHotel(rowA, rowB) {
  const isASubRow = rowA[COLUMN_PROPERTIES.HOTELINFO].isChildRow;
  const isBSubRow = rowB[COLUMN_PROPERTIES.HOTELINFO].isChildRow;

  if (isBSubRow && !isASubRow) {
    return -1;
  }

  if (isASubRow && !isBSubRow) {
    return 1;
  }

  return 0;
}

// eslint-disable-next-line max-params
export function sortHelper(rowA, rowB, subtype, column, sortDirection) {
  const valueKey = `${subtype}${SUBTYPE_DELIMITER}${column}`;

  const objectA = rowA[subtype]?.[column] || rowA[valueKey];
  const objectB = rowB[subtype]?.[column] || rowB[valueKey];

  // When both objects have no data, subsort use the subsort order:
  if (objectA?.hasNoData && objectB?.hasNoData) {
    return objectB.subsortOrder - objectA.subsortOrder;
  }

  // When one of the objects is undefined, automatically place them as last. This case catches the empty cells of the roomtype table
  if (!objectA || objectA.hasNoData) {
    return 1;
  }

  if (!objectB || objectB.hasNoData) {
    return -1;
  }

  // Cutoff policies consist of a day value & time value. In case of same day values, we can use the time value to sort more accurately
  if (column === COLUMN_PROPERTIES.CUT_OFF_POLICY) {
    if (objectA.value === objectB.value) {
      if (sortDirection === SORT_ORDER.DESCENDING) {
        return objectA.timeValueForSorting - objectB.timeValueForSorting;
      }
      return objectB.timeValueForSorting - objectA.timeValueForSorting;
    }
  }

  // Alphanumeric sort
  if (column === COLUMN_PROPERTIES.ROOM_NAME) {
    if (sortDirection === SORT_ORDER.DESCENDING) {
      return objectB.formattedValue.localeCompare(objectA.formattedValue);
    }
    return objectA.formattedValue.localeCompare(objectB.formattedValue);
  }

  // Numeric sort
  if (sortDirection === SORT_ORDER.DESCENDING) {
    return objectB.value - objectA.value;
  }

  return objectA.value - objectB.value;
}

// We don't want to use the hotel-and-marker service rate formatter: 1) shows currency symbol 2) toString() shows &rlm;/&lrm; HTML entities
// We want to show the rate values and "Sold out"/"--"" for missing data (API returns rate 0)
export function formatTableRate(rateObject) {
  if (rateObject.hasNoData) {
    return rateObject.formattedValue;
  }

  return rateObject.value;
}

// eslint-disable-next-line max-params
export function createCompareRedirectUrl(
  host,
  currentRouteName,
  columnKey,
  subtypeAttributes,
  shouldRedirectToSecondaryCompset,
  hotelAndMarketService,
) {
  // Pass the default QPs so we don't have to abort and restart the transition in the QP validation
  const queryParams = {
    los: hotelAndMarketService.hotelOrMarket.defaultLosRates[0],
    maxPersons: hotelAndMarketService.hotelOrMarket.defaultPersons,
    compsetId: shouldRedirectToSecondaryCompset ? 2 : 1,
    otaId: OTA_IDS.BOOKINGDOTCOM,
  };

  // Leadtime should redirect to Rates evolution detail (previous day)
  if (currentRouteName === 'hotel.strategy.leadtime') {
    queryParams.type = DETAIL_TABS.RATE_EVOLUTION;
    queryParams.date = hotelAndMarketService
      .getLocalTime()
      .subtract(1, 'day')
      .format('YYYY-MM-DD');
    return formatUrl(
      `/hotel/${hotelAndMarketService.hotel.id}/rates/detail`,
      queryParams,
      host,
    );
  }

  // All other detail views redirect to the Rates table
  queryParams.view = 'table';

  if (currentRouteName === 'hotel.strategy.los') {
    queryParams.los = columnKey;
    queryParams.compareType = COMPARETYPES.LOS;
    queryParams.compareValue = 1;
  } else if (
    currentRouteName === 'hotel.strategy.cancellation' &&
    columnKey === TYPE_ID_NON_REF
  ) {
    queryParams.rateParam = RATE_PARAMS.LOWEST;
    queryParams.compareType = COMPARETYPES.RATE_PARAM;
    queryParams.compareValue = RATE_PARAMS.BEST_FLEX;
  } else if (currentRouteName === 'hotel.strategy.mealtype') {
    if (
      columnKey === `${MEALTYPES.WALK_IN_BREAKFAST}` ||
      columnKey === `${MEALTYPES.INCLUSIVE_BREAKFAST}`
    ) {
      // Walk-in breakfast and Incluse breakfast both should redirect to bed and breakfast
      queryParams.mealPlan = MEALTYPES.BED_AND_BREAKFAST;
    } else {
      queryParams.mealPlan = columnKey;
    }
    // Enable compare for all mealtypes except Room Only
    if (columnKey !== MEALTYPES.ROOM_ONLY) {
      queryParams.compareType = COMPARETYPES.MEALTYPE;
      queryParams.compareValue = MEALTYPES.ROOM_ONLY;
    }
  } else if (currentRouteName === 'hotel.strategy.mobile') {
    queryParams.otaId = subtypeAttributes?.isMobileExpediaSubtype
      ? OTA_IDS.EXPEDIA
      : OTA_IDS.BOOKINGDOTCOM;
    queryParams.platform = columnKey;
    queryParams.compareType = COMPARETYPES.PLATFORM;
    queryParams.compareValue = PLATFORMS.PLATFORM_UNSPECIFIED;
  } else if (currentRouteName === 'hotel.strategy.membership') {
    queryParams.otaId = OTA_IDS.BRANDDOTCOM;
    queryParams.membershipType = MEMBERSHIPTYPES.MEMBER_ONLY;
    queryParams.compareType = COMPARETYPES.MEMBERSHIPTYPE;
    queryParams.compareValue = MEMBERSHIPTYPES.PUBLIC;
  } else if (currentRouteName === 'hotel.strategy.roomtype') {
    queryParams.roomType = columnKey;
    // Enable compare for all roomtypes except Standard
    if (columnKey !== 'standard') {
      queryParams.compareType = COMPARETYPES.ROOMTYPE;
      queryParams.compareValue = 'standard';
    }
  }

  return formatUrl(
    `/hotel/${hotelAndMarketService.hotel.id}/rates`,
    queryParams,
    host,
  );
}
